import React from 'react';
import { PromoCode, TTotal, Tax, ShopCart, ShopCartItem } from '../models';

type TUseShopCartTally = {
  shopCart: ShopCart;
  appliedDiscounts: PromoCode[];
  productsShopItems: ShopCartItem[];
  benefitsShopItems: ShopCartItem[];
  plansShopItems: ShopCartItem[];
};

export function useShopCartTally({
  shopCart,
  appliedDiscounts,
  productsShopItems,
  benefitsShopItems,
  plansShopItems,
}: TUseShopCartTally) {
  const [total, setTotal] = React.useState<TTotal>({
    subtotal: 0,
    totalTax: 0,
    total: 0,
    discountTotal: 0,
  });

  React.useEffect(() => {
    const _total = calculateShopcartTally({
      shopCart,
      productsShopItems,
      benefitsShopItems,
      plansShopItems,
      appliedDiscounts,
    });
    setTotal(_total);
  }, [shopCart, productsShopItems, benefitsShopItems, plansShopItems]);

  return total;
}

function calculateShopcartTally({
  shopCart,
  productsShopItems,
  benefitsShopItems,
  plansShopItems,
  appliedDiscounts,
}: TUseShopCartTally) {
  const { products, benefits, subscriptions } = shopCart;

  const _total = {
    subtotal: 0,
    taxableSubtotal: 0,
    totalTax: 0,
    total: 0,
    realTax: 0,
    discountTotal: 0,
  };

  // Return the total percentage and total fixed discounts
  const _discounts = appliedDiscounts.reduce(
    (acc, d) => [
      acc[0] + (d.type === 'percentage' ? d.value : 0),
      acc[1] + (d.type === 'fixed' ? d.value : 0),
    ],
    [0, 0]
  );

  productsShopItems.forEach((item) => {
    const variantId = item.variant;
    const totalQuantity = item.shopList.reduce(
      (acc, shopItem) => acc + shopItem.quantity,
      0
    );
    const product = products.find(
      (productItem) => productItem.variant._id === variantId
    );
    const variant = product?.variant;
    if (variant) {
      const totalPerVariant = (variant?.price ?? 0) * totalQuantity;
      const discountPerVariant = (totalPerVariant * _discounts[0]) / 100;

      // calculate subtotal
      _total.subtotal += totalPerVariant;
      // calculate taxableSubtotal
      _total.taxableSubtotal += product?.taxes?.length ? totalPerVariant : 0;
      // calculate discountTotal
      _total.discountTotal += discountPerVariant;
      // calculate totalTax and realTax
      const [_totalTax, _realTax] = (product?.taxes as Tax[])?.reduce(
        (_acc, tax) => [
          _acc[0] + (totalPerVariant - discountPerVariant) * (tax.value / 100),
          _acc[0] + totalPerVariant * (tax.value / 100),
        ],
        [0, 0]
      ) ?? [0, 0];

      _total.totalTax += _totalTax;
      _total.realTax += _realTax;
    }
  });

  subscriptions.forEach(({ quantity, price }) => {
    _total.subtotal += (price ?? 0) * (quantity ?? 1);
  });

  // benefit has benefit price
  benefitsShopItems.forEach((benefitGroup) => {
    // calculate total with benefit price
    const _benefitsTotal = benefitGroup.shopList.map(
      (b) => b.quantity * benefitsShopItems[0].price
    );
    _total.subtotal += _benefitsTotal.reduce((acc, i) => acc + i, 0);
  });

  _total.discountTotal += _discounts[1];
  // calculate total
  _total.total = _total.subtotal + _total.totalTax - _total.discountTotal;

  return _total;
}
